import { useMemo, useState } from 'react';
import {
  Card,
  CardHeader,
  Badge,
  Button,
  Input,
  Select,
  LoadingSkeleton,
} from '@nexora/design-system';
import { useTaxRates } from '../tax-rates/useTaxRates';
import { formatDate } from '../../../shared/utils/format';

/* ───────────────────────────────────────────────────────────────────────────
   Portado de base-html, slug `erpone_matriz_de_exce_es_fiscais_uf_x_uf`
   → Matriz de Exceções Fiscais UF × UF.

   NÃO EXISTE rota /fiscal/uf-matrix no backend (verificado nos controllers
   do módulo fiscal). A matriz é DERIVADA de GET /fiscal/tax-rates, que
   aceita filtros `taxType`, `ufOrigem`, `ufDestino`, `ncmPrefix`
   (QueryTaxRateDto). Cada célula (origem → destino) mostra a alíquota de
   ICMS efetiva configurada.

   Limitações honestas:
   * o filtro de UF do backend mantém as alíquotas SEM UF (valem para
     qualquer cenário) — a célula cai no fallback "qualquer" quando não há
     par específico;
   * o endpoint é paginado (máx 200 por página) — a matriz usa limit=200 e,
     se o tenant tiver mais alíquotas, mostra um aviso em vez de mentir;
   * "MVA Ajustada/Original", "Convênio ICMS 52/91" e "Legal Basis" do
     protótipo não existem no contrato — a matriz mostra o que a API devolve.

   Rotas consumidas:
     - GET /fiscal/tax-rates?taxType=ICMS&limit=200 (fonte da matriz)
   ─────────────────────────────────────────────────────────────────────────── */

const UFS = [
  'AC', 'AL', 'AM', 'AP', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MG', 'MS',
  'MT', 'PA', 'PB', 'PE', 'PI', 'PR', 'RJ', 'RN', 'RO', 'RR', 'RS', 'SC',
  'SE', 'SP', 'TO',
];

/** Alíquota efetiva de uma TaxRate (mesmo cálculo de isInForce + redução). */
function effectiveRate(rate: { aliquota: number; reducao: number | null }): number {
  const reducao = rate.reducao ?? 0;
  return reducao > 0 ? rate.aliquota * (1 - reducao / 100) : rate.aliquota;
}

function formatRate(v: number | null | undefined): string {
  if (v === null || v === undefined || Number.isNaN(v)) return '—';
  return `${Number(v).toFixed(1).replace('.', ',')}%`;
}

export function UfExceptionMatrixPage(): JSX.Element {
  const [taxType, setTaxType] = useState('ICMS');
  const [ncmPrefix, setNcmPrefix] = useState('');
  const [ufFilter, setUfFilter] = useState('');

  const { data, isLoading, error, refetch } = useTaxRates({
    taxType,
    ncmPrefix: ncmPrefix || undefined,
    page: 1,
    limit: 200,
  });

  const rates = data?.data ?? [];

  /* Matriz origin × destination → alíquota efetiva (preferência régua:
     1. par exato (ufOrigem & ufDestino); 2. origem específica genérica;
     3. destino específico genérico; 4. alíquota sem UF). */
  const matrix = useMemo(() => {
    const rows = new Map<string, Map<string, { value: number; ncmPrefix: string | null; effectiveSince: string }>>();
    for (const rate of rates) {
      const origin = rate.ufOrigem ?? '*';
      const dest = rate.ufDestino ?? '*';
      if (!rows.has(origin)) rows.set(origin, new Map());
      rows.get(origin)?.set(dest, { value: effectiveRate(rate), ncmPrefix: rate.ncmPrefix, effectiveSince: rate.effectiveSince });
    }
    return rows;
  }, [rates]);

  const originUfs = useMemo(() => {
    const set = new Set<string>(UFS);
    for (const origin of matrix.keys()) if (origin !== '*') set.add(origin);
    return [...set].sort();
  }, [matrix]);

  const visibleOrigins = ufFilter ? [ufFilter] : originUfs;

  function cellFor(origin: string, dest: string): { value: number; ncmPrefix: string | null; effectiveSince: string } | null {
    return (
      matrix.get(origin)?.get(dest) ??
      matrix.get(origin)?.get('*') ??
      matrix.get('*')?.get(dest) ??
      matrix.get('*')?.get('*') ??
      null
    );
  }

  return (
    <div className="uf-matrix-page">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-md mb-lg">
        <div>
          <h1 className="font-display-lg text-display-lg text-on-surface m-0">
            Matriz de Exceções Fiscais UF × UF
          </h1>
          <p className="font-body-md text-body-md text-on-surface-variant mt-xs">
            Alíquotas configuradas por cenário de UF — derivada de GET /fiscal/tax-rates (não há rota /fiscal/uf-matrix).
          </p>
        </div>
      </div>

      {/* Filtros */}
      <Card padding="1rem" variant="outlined" className="filter-card">
        <div className="matrix-filters">
          <Select label="Tributo" value={taxType} onChange={(e) => setTaxType(e.target.value)}>
            <option value="ICMS">ICMS</option>
            <option value="ICMS_ST">ICMS-ST</option>
            <option value="ICMS_DIFAL">ICMS-DIFAL</option>
            <option value="IPI">IPI</option>
            <option value="PIS">PIS</option>
            <option value="COFINS">COFINS</option>
            <option value="ISS">ISS</option>
            <option value="IBS">IBS</option>
            <option value="CBS">CBS</option>
          </Select>
          <Input
            label="Prefixo NCM"
            placeholder="Ex.: 8471"
            value={ncmPrefix}
            onChange={(e) => setNcmPrefix(e.target.value.replace(/\D/g, ''))}
          />
          <Select label="UF de origem" value={ufFilter} onChange={(e) => setUfFilter(e.target.value)}>
            <option value="">Todas</option>
            {UFS.map((uf) => <option key={uf} value={uf}>{uf}</option>)}
          </Select>
        </div>
      </Card>

      {/* Legenda */}
      <Card padding="0.75rem 1rem" variant="outlined" className="matrix-legend">
        <small>
          Células com <strong>“qualquer”</strong> usam uma alíquota sem UF específica (vale para o cenário inteiro).
          O backend mantém essas linhas mesmo quando se filtra por UF — é o comportamento do filtro, não erro.
        </small>
      </Card>

      {/* Matriz */}
      <Card variant="outlined" padding="0">
        <CardHeader className="px-md pt-md" action={
          <span className="font-label-md text-label-md text-on-surface-variant tabular-nums">
            {data?.total ?? 0} alíquotas de {taxType}
          </span>
        }>
          <h3 className="font-headline-sm text-headline-sm text-on-surface m-0">Origem ↓ → Destino →</h3>
        </CardHeader>
        {isLoading ? (
          <div className="matrix-loading"><LoadingSkeleton count={8} height="36px" /></div>
        ) : error ? (
          <div className="matrix-empty">
            <p style={{ fontWeight: 600, color: '#ba1a1a', margin: '0 0 0.5rem' }}>Erro ao carregar alíquotas</p>
            <p style={{ fontSize: '0.875rem', margin: '0 0 1rem' }}>
              {error instanceof Error ? error.message : 'Erro desconhecido'}
            </p>
            <Button variant="primary" onClick={() => refetch()}>Tentar novamente</Button>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="matrix-table">
              <thead>
                <tr>
                  <th className="matrix-table__corner">Origem \ Destino</th>
                  {UFS.map((uf) => <th key={uf} className="matrix-table__uf">{uf}</th>)}
                </tr>
              </thead>
              <tbody>
                {visibleOrigins.map((origin) => (
                  <tr key={origin}>
                    <td className="matrix-table__origin"><strong>{origin}</strong></td>
                    {UFS.map((dest) => {
                      const cell = cellFor(origin, dest);
                      const isGeneric = cell !== null && (() => {
                        const exact = matrix.get(origin)?.get(dest) ?? null;
                        return exact === null;
                      })();
                      return (
                        <td key={dest} className="matrix-table__cell">
                          {cell === null ? (
                            <span className="matrix-table__empty">—</span>
                          ) : (
                            <span
                              className={`matrix-table__rate${isGeneric ? ' matrix-table__rate--generic' : ''}`}
                              title={`${cell.ncmPrefix ? `NCM ${cell.ncmPrefix}` : 'NCM qualquer'} • vigência ${formatDate(cell.effectiveSince)}`}
                            >
                              {formatRate(cell.value)}
                            </span>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {!isLoading && !error && (data?.total ?? 0) >= 200 && (
          <div className="matrix-note">
            <Badge size="sm" variant="warning">Atenção</Badge>
            <small>
              O tenant possui 200+ alíquotas de {taxType}; o endpoint devolve no máximo 200 por página
              (QueryTaxRateDto.limit máx 200). A matriz cobre a primeira página — refina por UF/NCM para ver o resto.
            </small>
          </div>
        )}
        {!isLoading && !error && (data?.total ?? 0) === 0 && (
          <div className="matrix-empty">
            <p className="m-0">Nenhuma alíquota de {taxType} configurada.</p>
          </div>
        )}
      </Card>

      <style>{`
        .uf-matrix-page { padding: 1.5rem; }
        .matrix-filters { display: flex; gap: 0.75rem; align-items: flex-end; flex-wrap: wrap; }
        .matrix-filters > * { flex: 1; min-width: 160px; }
        .matrix-legend { margin-bottom: 1rem; color: #74777f; }
        .matrix-loading, .matrix-empty { padding: 2rem; text-align: center; display: grid; gap: 0.5rem; justify-items: center; color: #74777f; }
        .matrix-note { display: flex; align-items: center; gap: 0.5rem; padding: 0.75rem 1rem; border-top: 1px solid #eceef0; color: #74777f; }
        .matrix-table { width: 100%; border-collapse: collapse; font-size: 0.75rem; }
        .matrix-table th {
          padding: 0.5rem 0.375rem; background: #f2f4f6; color: #43474e; font-weight: 600;
          text-transform: uppercase; letter-spacing: 0.04em; border-bottom: 2px solid #e0e3e5;
          text-align: center; white-space: nowrap; position: sticky; top: 0; z-index: 1;
        }
        .matrix-table__corner { text-align: left !important; min-width: 90px; }
        .matrix-table__uf { min-width: 44px; }
        .matrix-table td { padding: 0.25rem 0.375rem; text-align: center; border-bottom: 1px solid #f2f4f6; }
        .matrix-table__origin { text-align: left !important; font-size: 0.8125rem; }
        .matrix-table__cell { white-space: nowrap; }
        .matrix-table__rate { display: inline-block; background: #eef2f7; border-radius: 6px; padding: 0.125rem 0.375rem; font-weight: 600; color: #002045; font-variant-numeric: tabular-nums; }
        .matrix-table__rate--generic { background: #f7f8fa; color: #74777f; font-weight: 400; }
        .matrix-table__empty { color: #d3d6db; }
        .matrix-table tbody tr:hover td { background: #f8fafc; }
      `}</style>
    </div>
  );
}